import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CalendarClock, Clock3, Info, Server } from "lucide-react";
import { toast } from "sonner";
import { adminPlatformSettingService } from "@/services/admin-platform-setting.service";
import type { VmSettingsDto } from "@/types/platform-setting";
import {
  vmSettingsSchema,
  type VmSettingsFormData,
} from "@/schemas/vm-settings";
import {
  SettingsField,
  SettingsFormFooter,
  SettingsFormSkeleton,
  SettingsLoadError,
  SettingsSection,
  SettingsSwitch,
} from "./SettingsForm";

const VM_SETTINGS_QUERY_KEY = ["admin", "platform-settings", "vm"];

function settingsToForm(settings: VmSettingsDto): VmSettingsFormData {
  return {
    reinstallLimitEnabled: settings.reinstall.limitEnabled,
    reinstallTrialLimit: String(settings.reinstall.trialLimit),
    reinstallPaidLimitPerMonth: String(settings.reinstall.paidLimitPerMonth),
  };
}

/**
 * The platform-wide virtual machine settings, one section per concern. They are one JSON document on
 * the server (GET/PUT /platform-settings/vm), so a new VM setting is a new field in a section here —
 * or a new SettingsSection — and nothing else on this page changes.
 */
export function VmSettingsTab() {
  const queryClient = useQueryClient();

  const {
    data: vmSettings,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: VM_SETTINGS_QUERY_KEY,
    queryFn: () => adminPlatformSettingService.getVmSettings(),
  });

  const form = useForm<VmSettingsFormData>({
    resolver: zodResolver(vmSettingsSchema),
    defaultValues: {
      reinstallLimitEnabled: false,
      reinstallTrialLimit: "",
      reinstallPaidLimitPerMonth: "",
    },
  });

  useEffect(() => {
    if (vmSettings) {
      form.reset(settingsToForm(vmSettings));
    }
  }, [vmSettings, form]);

  const updateMutation = useMutation({
    mutationFn: (data: VmSettingsFormData) =>
      adminPlatformSettingService.updateVmSettings({
        reinstall: {
          limitEnabled: data.reinstallLimitEnabled,
          trialLimit: Number(data.reinstallTrialLimit),
          paidLimitPerMonth: Number(data.reinstallPaidLimitPerMonth),
        },
      }),
    onSuccess: (saved) => {
      // The response is the settings now in force, so the form takes them without a second request.
      // Nothing else is invalidated: no other tab is mounted, and each refetches when it is opened.
      queryClient.setQueryData(VM_SETTINGS_QUERY_KEY, saved);
      toast.success("VM settings saved.");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const { errors, isDirty } = form.formState;
  const limitEnabled = form.watch("reinstallLimitEnabled");

  return (
    <div className="grid items-start gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
      <Card className="overflow-hidden shadow-sm">
        <CardHeader className="border-b bg-muted/30 p-5">
          <CardTitle className="flex items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Server className="size-4" />
            </span>
            Virtual Machine Settings
          </CardTitle>
          <CardDescription className="max-w-2xl leading-6">
            Rules that apply to every customer&apos;s virtual machines.
          </CardDescription>
        </CardHeader>

        {isLoading ? (
          <SettingsFormSkeleton rows={2} />
        ) : isError ? (
          <SettingsLoadError onRetry={() => void refetch()} />
        ) : (
          <form
            onSubmit={form.handleSubmit((data) => updateMutation.mutate(data))}
          >
            <CardContent className="divide-y p-0">
              <SettingsSection title="Reinstall limits">
                {/* The numbers stay editable while the switch is off, so an operator can set them
                    first and switch the limits on afterwards. */}
                <SettingsSwitch
                  id="reinstallLimitEnabled"
                  label="Enable reinstall limits"
                  description={
                    limitEnabled
                      ? "The limits below are switched on."
                      : "The limits below are switched off. Their numbers are kept."
                  }
                  checked={limitEnabled}
                  onCheckedChange={(checked) =>
                    form.setValue("reinstallLimitEnabled", checked, {
                      shouldDirty: true,
                    })
                  }
                />
                <div className="grid gap-4 md:grid-cols-2">
                  <SettingsField
                    id="reinstallTrialLimit"
                    label="Trial VM"
                    error={errors.reinstallTrialLimit?.message}
                  >
                    <Input
                      id="reinstallTrialLimit"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="3"
                      {...form.register("reinstallTrialLimit")}
                    />
                  </SettingsField>
                  <SettingsField
                    id="reinstallPaidLimitPerMonth"
                    label="Paid VM, per month"
                    error={errors.reinstallPaidLimitPerMonth?.message}
                  >
                    <Input
                      id="reinstallPaidLimitPerMonth"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="10"
                      {...form.register("reinstallPaidLimitPerMonth")}
                    />
                  </SettingsField>
                </div>
              </SettingsSection>
            </CardContent>

            <SettingsFormFooter
              isDirty={isDirty}
              isSaving={updateMutation.isPending}
              onDiscard={() =>
                form.reset(vmSettings ? settingsToForm(vmSettings) : undefined)
              }
            />
          </form>
        )}
      </Card>

      {/* What the switch and the two numbers do once saved, so an operator knows who is limited and
          when a count starts again. */}
      <Card className="shadow-sm">
        <CardHeader className="p-5 pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Info className="size-4 text-primary" /> How the limits apply
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0">
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex gap-2.5">
              <Clock3 className="mt-0.5 size-4 shrink-0" />
              <span>
                Only a customer is stopped at the limit. Every reinstall that
                completes is counted, including one started from this console
                and those done while the limits were switched off.
              </span>
            </li>
            <li className="flex gap-2.5">
              <CalendarClock className="mt-0.5 size-4 shrink-0" />
              <span>
                Trial VM is the number for the whole trial. Paid VM is the
                number for each month of the billing period. A renewal starts
                again from zero on the first day of the new period. 0 means no
                limit.
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
